import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useAuth, usePermissions } from '@/features/auth';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { formatDateTime } from '@/shared/lib/format';

import { useBalanceApprovals, useDecideBalanceApproval } from '../api/providers.queries';
import type { BalanceApproval } from '../types/provider.types';

const REASON_CLASS =
  'border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent p-2 text-sm outline-none focus-visible:ring-[3px]';

type Decision = 'approve' | 'reject';

/**
 * Balance adjustments above the approval threshold, waiting for someone other
 * than the requester (providers.balance.approve). Hidden when there are none.
 */
export function BalanceApprovalsPanel({ providerId }: { providerId: number }) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const approvals = useBalanceApprovals(providerId);
  const decide = useDecideBalanceApproval();
  const [deciding, setDeciding] = useState<{
    approval: BalanceApproval;
    decision: Decision;
  } | null>(null);
  const [reason, setReason] = useState('');

  const items = approvals.data ?? [];
  if (items.length === 0) return null;

  const canApprove = can('providers.balance.approve');
  const rejecting = deciding?.decision === 'reject';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Waiting for approval</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y text-sm">
          {items.map((approval) => {
            const own = user?.id === approval.requestedByUserId;
            return (
              <li
                key={approval.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div className="min-w-0">
                  <p className="font-medium">{approval.summary}</p>
                  <p className="text-muted-foreground text-xs">
                    {approval.requestedByName ?? '—'} · {formatDateTime(approval.requestedAtUtc)}
                    {approval.requestReason ? ` · ${approval.requestReason}` : ''}
                  </p>
                </div>
                <span className="flex gap-1.5">
                  {canApprove && !own && (
                    <>
                      <Button
                        size="sm"
                        className="h-7"
                        onClick={() => {
                          setReason('');
                          setDeciding({ approval, decision: 'approve' });
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7"
                        onClick={() => {
                          setReason('');
                          setDeciding({ approval, decision: 'reject' });
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {own && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      disabled={decide.isPending}
                      onClick={() =>
                        decide.mutate({ approvalId: approval.id, decision: 'cancel', reason: null })
                      }
                    >
                      Withdraw
                    </Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>

      <ConfirmDialog
        open={deciding !== null}
        onOpenChange={(open) => !open && setDeciding(null)}
        title={rejecting ? 'Reject this adjustment?' : 'Approve this adjustment?'}
        description={
          rejecting
            ? `${deciding?.approval.summary ?? ''}. The balance does not change.`
            : `${deciding?.approval.summary ?? ''}. The balance changes now and the change is audited.`
        }
        confirmLabel={rejecting ? 'Reject' : 'Approve'}
        variant={rejecting ? 'destructive' : 'default'}
        isPending={decide.isPending}
        confirmDisabled={rejecting && reason.trim().length < 3}
        onConfirm={() => {
          if (!deciding) return;
          const trimmed = reason.trim();
          decide.mutate(
            {
              approvalId: deciding.approval.id,
              decision: deciding.decision,
              reason: trimmed === '' ? null : trimmed,
            },
            { onSuccess: () => setDeciding(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="balance-decision-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              {rejecting ? '(required)' : '(optional)'}
            </span>
          </Label>
          <textarea
            id="balance-decision-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={2}
            maxLength={500}
            className={REASON_CLASS}
          />
        </div>
      </ConfirmDialog>
    </Card>
  );
}
